import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import api, { jobDescriptionAPI } from '../services/api';
import { getNextPendingStep } from '../utils/courseFlow';
import SignPasswordModal from '../components/SignPasswordModal';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';

// SignJobDescription — pagina de semnare a fișei postului pentru oricare semnatar (employee
// sau counterpart). Backend-ul determină slotul automat în funcție de cine apelează. Pagina
// afișează PDF-ul preview (iframe) + buton „Semnează" care deschide SignPasswordModal (același
// modal AdES folosit la teste / fișe / admis la lucru) — astfel UX-ul e identic peste tot.
// După sign reușit, redirect imediat la /my-courses (sau /sheets-to-sign pentru counterpart)
// ca să eviti re-click pe Semnează care ar arunca „ai semnat deja".
const SignJobDescription = () => {
  const { t } = useTranslation();
  const { instanceId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [justSigned, setJustSigned] = useState(false);
  // Pop-up Felicitări — apare după sign reușit, cu buton de continuare la următorul pas
  // (IIG_SU/ILM_SU dacă mai sunt cursuri, /my-courses altfel). Aliniat cu UX-ul din SignaturePage.
  const [showCongratsModal, setShowCongratsModal] = useState(false);
  const [nextStepLink, setNextStepLink] = useState(null);
  const [nextStepLabel, setNextStepLabel] = useState('');

  useEffect(() => {
    let revokeFn = null;
    let mounted = true;
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(jobDescriptionAPI.getPdfUrl(instanceId), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (!mounted) return;
        const url = URL.createObjectURL(blob);
        revokeFn = () => URL.revokeObjectURL(url);
        setPdfUrl(url);
      } catch (err) {
        showToast(
          t('jobDescription.errorLoadingPdf', 'Eroare la încărcarea documentului'),
          'error'
        );
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
      if (revokeFn) revokeFn();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  // După sign, determinăm următorul pas pentru flow cursiv. Returnează { link, label }.
  // - Dacă tocmai am semnat ca employee fișa postului SSM și există IIG_SU disponibil → IIG_SU.
  // - Dacă onboarding-ul s-a terminat (am semnat FP-ul ultimului domeniu) și există IP-uri →
  //   prima IP rămasă (helperul le plimbă pe TOATE, una după alta).
  // - Altfel → /my-courses (vede tot ce a făcut).
  // - Pentru counterpart (admin care a semnat pe altcineva) → /sheets-to-sign.
  const resolveNextStep = async (signedFlow) => {
    try {
      const ssm = signedFlow?.ssm;
      const su = signedFlow?.su;
      const justSignedDomain =
        ssm?.instanceId === instanceId ? 'SSM' : su?.instanceId === instanceId ? 'SU' : null;

      const courses = await api.get('/courses/my/assigned').catch(() => ({ data: [] }));
      const assignments = courses.data || [];

      if (justSignedDomain === 'SSM') {
        for (const type of ['IIG_SU', 'ILM_SU']) {
          const a = assignments.find((x) => x.course?.type === type);
          if (a && a.training?.status !== 'PASSED') {
            const shortType = type === 'IIG_SU' ? 'IIG' : 'ILM';
            return {
              link: `/training/${a.course.id}`,
              label: t('jobDescription.continueToSU', { type: shortType }),
            };
          }
        }
      }
      // Trecem la faza periodică cu continuitate: toate IP SSM → FP SSM → toate IP SU → FP SU.
      // Pasăm `signedFlow` (flow-ul de DUPĂ semnare) — FP-ul tocmai semnat e WAITING_COUNTERPART acum,
      // deci se sare automat la pasul următor (ex: după FP SSM → IP SU, apoi FP SU).
      const ipStep = getNextPendingStep(assignments, signedFlow);
      if (ipStep) return ipStep;

      return { link: '/my-courses', label: t('jobDescription.viewMyCourses') };
    } catch (_) {
      return { link: '/my-courses', label: t('jobDescription.viewMyCourses') };
    }
  };

  // Submit-ul SignPasswordModal — arunc dacă eșuează ca modalul să rămână deschis cu eroarea.
  const submitSign = async (password) => {
    await jobDescriptionAPI.sign(instanceId, password);
    // Succes — închidem modalul de parolă, marcăm ca semnat
    setJustSigned(true);
    setShowPwdModal(false);
    showToast(t('jobDescription.signed', 'Ai semnat fișa postului'), 'success');
    // Determinăm următorul pas + afișăm pop-up Felicitări (aliniat cu UX-ul de pe SignaturePage)
    const flowRes = await jobDescriptionAPI.getMyFlow().catch(() => ({ data: {} }));
    const next = await resolveNextStep(flowRes.data || {});
    setNextStepLink(next.link);
    setNextStepLabel(next.label);
    setShowCongratsModal(true);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            {t('jobDescription.signPageTitle', 'Fișa postului — semnare')}
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            {t(
              'jobDescription.signPageSubtitle',
              'Verifică conținutul, apoi semnează cu parola ta.'
            )}
          </p>
        </div>
        <Link
          to="/my-courses"
          className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-100"
        >
          ← {t('common.back', 'Înapoi')}
        </Link>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm ring-1 ring-slate-200 dark:ring-slate-800 overflow-hidden mb-4">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          </div>
        ) : pdfUrl ? (
          <iframe
            title={t('jobDescription.pdfIframeTitle')}
            src={pdfUrl}
            className="w-full"
            style={{ height: '70vh', minHeight: '500px' }}
          />
        ) : (
          <div className="text-center py-10 text-slate-500">
            {t('jobDescription.pdfUnavailable', 'Document indisponibil')}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => setShowPwdModal(true)}
          disabled={justSigned}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition"
        >
          {justSigned
            ? t('jobDescription.signed', 'Ai semnat fișa postului')
            : t('jobDescription.openSignModal', 'Semnează documentul')}
        </button>
      </div>

      {showPwdModal && (
        <SignPasswordModal
          title={t('jobDescription.modalTitle', 'Semnează cu parola ta')}
          description={t(
            'jobDescription.modalDescription',
            'Parola decriptează cheia de semnătură electronică avansată (AdES) și aplică semnătura pe fișa postului.'
          )}
          onSubmit={submitSign}
          onClose={() => setShowPwdModal(false)}
        />
      )}

      {/* Pop-up Felicitări după sign — aliniat cu UX-ul de pe SignaturePage. Permite utilizatorului
          să continue cursiv la următorul pas (IIG_SU/ILM_SU) sau să rămână pe pagină. */}
      <Modal
        open={showCongratsModal}
        onClose={() => setShowCongratsModal(false)}
        title={t('jobDescription.congratsTitle')}
        footer={
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <Button variant="ghost" onClick={() => setShowCongratsModal(false)}>
              {t('jobDescription.stayOnPage')}
            </Button>
            {nextStepLink && (
              <Button
                variant="accent"
                onClick={() => {
                  setShowCongratsModal(false);
                  navigate(nextStepLink);
                }}
              >
                {nextStepLabel || t('jobDescription.continueDefault')} →
              </Button>
            )}
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-700 dark:text-slate-300">
            {t('jobDescription.congratsBody')}
          </p>
          {nextStepLink && nextStepLink !== '/my-courses' && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('jobDescription.moreTrainingsNote')}
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default SignJobDescription;
